import { Inject, Injectable } from '@angular/core'
import jwt_decode from 'jwt-decode'
import { isEqual } from 'lodash'
import { BehaviorSubject, distinctUntilChanged } from 'rxjs'
import { map } from 'rxjs/operators'
import * as Sentry from '@sentry/angular'
import { GbCookieService } from '../gb-cookie/gb-cookie.service'
import { APP_CONFIG } from 'libs/shared/app-config/src/lib/gb-app-config'

export interface BaseDecodedToken {
  p?: string // guest
  iat: number
  exp: number
}

@Injectable({
  providedIn: 'root',
})
export class GbBaseAuthService {
  private accessTokenSubject$ = new BehaviorSubject<string | undefined>(this.getAccessToken())
  accessToken$ = this.accessTokenSubject$.asObservable().pipe(distinctUntilChanged((prev, curr) => isEqual(prev, curr)))
  isAuthenticated$ = this.accessToken$.pipe(
    map((token) => !!token),
    distinctUntilChanged(),
  )

  get accessTokenCookieName(): string {
    return this.appConfig.accessTokenCookieName
  }

  /**
   * Current value of the internal BehaviorSubject.
   * Used as a fallback by interceptor and decoder if the physical cookie is removed.
   */
  get accessTokenSubjectValue(): string | undefined {
    return this.accessTokenSubject$.getValue()
  }

  constructor(
    protected gbCookieService: GbCookieService,
    @Inject(APP_CONFIG) protected appConfig: any,
  ) {}

  setAccessToken(token: string | undefined): void {
    this.gbCookieService.setAccessToken(token)
    this.accessTokenSubject$.next(token)
  }

  getAccessToken(): string | undefined {
    if (!this.gbCookieService.getAccessToken()) return undefined

    let token = this.gbCookieService.getAccessToken()

    if (!token) {
      Sentry.captureMessage(`Token is undefined or empty for ${this.accessTokenCookieName}`)
      return undefined
    }

    try {
      token = JSON.parse(token)
    } catch (err) {
      Sentry.captureMessage(`SyntaxError: ${err}, ${token.substring(0, 20)}`)
      return undefined
    }

    return token && Object.keys(token).length ? Object.values(token)[0] : undefined
  }

  getDecodedAccessToken(token?: string): BaseDecodedToken | undefined {
    if (!token) {
      token = this.getAccessToken() || this.accessTokenSubjectValue // fallback to subject if cookie removed
    }
    return token ? jwt_decode(token) : undefined
  }

  isAuthenticated(): boolean {
    return this.gbCookieService.hasAccessToken() || !!this.accessTokenSubjectValue // fallback to subject if cookie removed
  }

  /**
   * Used in the shared lib to check if the user is not a guest
   */
  isAdminOrVolunteer(token?: string): boolean {
    const decodedToken = this.getDecodedAccessToken(token)
    return decodedToken ? !decodedToken.p : false
  }

  clearAccessToken(): void {
    this.gbCookieService.deleteCookie(this.accessTokenCookieName)
    this.accessTokenSubject$.next(undefined)
  }
}
